import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { View, StyleSheet, Keyboard, TouchableOpacity, TouchableWithoutFeedback, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { StackScreenProps } from '@react-navigation/stack';
import {
  BaseAdminScreen,
  AdminEmptyScreenLayout,
  InlineEmptyMessage,
} from '@/components';
import { ConversationList } from '@/components/admin/messages/ConversationList';
import { ConversationListSkeleton } from '@/components/admin/messages/ConversationListSkeleton';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useConversations, useMessageFilters } from '@/hooks/admin/useMessages';
import { FilterType, FILTER_VALUES } from '@/types/admin/messages';
import { getFilterEmptyState, getNoResultsState } from '@/utils/admin/messages/emptyState';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopMessagesView } from './desktop/DesktopMessagesView';
import { useDebouncedValue } from '@/hooks/common/useDebouncedValue';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';
import { useAppSelector } from '@/store';
import { selectIsMenuOpen } from '@/store/selectors/adminUISelectors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { ChatGlassListHeader, PageHeader, TabSegment, TabSegmentItem } from '@/components/shared/redesign';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

type MessagesScreenProps = StackScreenProps<AdminStackParamList, 'Messages'>;

const DATATABLE_DEFAULTS = { paginationModel: { page: 0, pageSize: 25 } };

export const MessagesScreen: React.FC<MessagesScreenProps> = ({
  navigation,
}) => {
  const { redesign, semantic } = useAdminColors();
  const { layoutMode } = useResponsive();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 250);

  // Get navigation menu state to close sort menu when nav opens
  const isNavigationMenuOpen = useAppSelector(selectIsMenuOpen);

  const {
    conversations,
    isLoading,
    error,
    setConversationQuery,
    filterType,
    setFilterType,
    sortType,
    setSortType,
    hasNextPage,
    loadMore,
    markConversationAsReadLocally,
  } = useConversations();
  const { filterOptions, sortOptions } = useMessageFilters();

  // Remove debug logs; noop effect preserved intentionally if future side-effects are needed
  useEffect(() => {}, [conversations]);

  // Update search query when it changes
  React.useEffect(() => {
    setConversationQuery(debouncedSearch);
  }, [debouncedSearch]); // Remove setConversationQuery from dependencies

  // Determine if we have data to show in the list
  const hasData = conversations.length > 0;
  const hasSearchQuery = searchQuery.trim() !== '';

  // Show loading state if we're loading and have no data yet and no active search
  const showLoadingState = isLoading && !hasData && !hasSearchQuery;

  // Show error state if we have a critical error and no data and no active search
  const showErrorState = error && !hasData && !isLoading && !hasSearchQuery;

  // Handle conversation selection
  const handleConversationSelect = useCallback((conversationId: number) => {
    markConversationAsReadLocally(conversationId);
    navigation.navigate('Chat', { conversationId });
  }, [navigation, markConversationAsReadLocally]);

  // Handle create conversation
  const handleCreateConversation = useCallback(() => {
    navigation.navigate('CreateMessage');
  }, [navigation]);

  // Filter tab items for TabSegment
  const filterItems: TabSegmentItem<FilterType>[] = useMemo(() => {
    return (Object.keys(FILTER_VALUES) as FilterType[]).map((key) => ({
      key,
      label: FILTER_VALUES[key],
    }));
  }, []);

  // Sort options for ChatGlassListHeader
  const headerSortOptions = useMemo(() =>
    sortOptions.map((opt) => ({ key: opt.key, label: opt.value })),
  [sortOptions]);

  // Render empty state message for when no data but tabs should remain visible
  const renderEmptyMessage = () => {
    const cfg = getFilterEmptyState(filterType);
    return (
      <InlineEmptyMessage
        title={cfg.title}
        message={cfg.message}
        icon={cfg.icon}
        containerStyle={styles.emptyMessageContainer}
        titleStyle={{ ...styles.emptyMessageText, color: redesign.text.primary }}
        messageStyle={{ ...styles.emptyMessageSubtext, color: redesign.text.muted }}
      />
    );
  };

  // Render error state
  const renderErrorState = () => (
    <View style={styles.errorContainer}>
      <SVG_COMPONENTS.X_CIRCLE width={48} height={48} color={semantic.status.error} />
      <Text style={[styles.errorTitle, { color: redesign.text.primary }]}>
        Unable to Load Conversations
      </Text>
      <Text style={[styles.errorMessage, { color: redesign.text.muted }]}>
        Something went wrong while loading the conversations.
      </Text>
    </View>
  );

  // Render content based on state
  const renderContent = () => {
    if (showLoadingState) {
      return <ConversationListSkeleton />;
    }

    if (showErrorState) {
      return renderErrorState();
    }

    return (
      <View style={styles.container}>
        {/* Header (search + sort) — compose lives in the page title bar.
            containerStyle zeroes the component's internal 24px padding so
            content aligns with PageHeader at the parent's 16px inset. */}
        <ChatGlassListHeader<string>
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortOptions={headerSortOptions}
          sortOption={sortType as string}
          onSortChange={(key) => setSortType(key as typeof sortType)}
          placeholder="Search conversations"
          containerStyle={styles.chatHeaderOverride}
        />

        {/* Filter tabs - always visible */}
        <View style={styles.filterContainer}>
          <TabSegment<FilterType>
            items={filterItems}
            selectedKey={filterType}
            onChange={(key) => setFilterType(key)}
            variant="pill"
            size="md"
          />
        </View>

        {/* Main content */}
        <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
          <View style={styles.content}>
            {hasData ? (
              <ConversationList
                conversations={conversations}
                hasNextPage={hasNextPage}
                isLoading={isLoading}
                loadMore={loadMore}
                onConversationSelect={handleConversationSelect}
              />
            ) : hasSearchQuery ? (
              (() => { const cfg = getNoResultsState(searchQuery); return (
                <InlineEmptyMessage
                  title={cfg.title}
                  message={cfg.message}
                  icon={cfg.icon}
                  containerStyle={styles.noResultsContainer}
                  titleStyle={{ ...styles.noResultsTitle, color: redesign.text.primary }}
                  messageStyle={{ ...styles.noResultsMessage, color: redesign.text.muted }}
                />);
              })()
            ) : (
              renderEmptyMessage()
            )}
          </View>
        </TouchableWithoutFeedback>
      </View>
    );
  };

  return (
    layoutMode === 'desktop' ? (
      <AdminErrorBoundary context="MessagesScreenDesktop">
        <DesktopMessagesView />
      </AdminErrorBoundary>
    ) : (
      <AdminErrorBoundary context="MessagesScreen">
        <DataTableProvider tableKey="admin:messages" defaults={DATATABLE_DEFAULTS}>
          <BaseAdminScreen
            screenName="Messages"
            navigation={navigation}
            showPlaceholder={false}
            scrollEnabled={false}
            contentContainerStyle={styles.baseScreenContent}
          >
            <AdminEmptyScreenLayout
              title="Messages"
              emptyStateType="messages"
              hasContent={true}
              disableScrollView={true}
              showTitleBar={false}
            >
              <PageHeader
                title="Messages"
                rightAction={
                  <TouchableOpacity
                    style={styles.composeButton}
                    onPress={handleCreateConversation}
                    activeOpacity={0.8}
                    accessibilityRole="button"
                    accessibilityLabel="New message"
                    accessibilityHint="Tap to start a new message"
                  >
                    <SVG_COMPONENTS.PENCIL width={20} height={20} color={PRIMITIVE_COLORS.white} />
                  </TouchableOpacity>
                }
              />
              {renderContent()}
            </AdminEmptyScreenLayout>
          </BaseAdminScreen>
        </DataTableProvider>
      </AdminErrorBoundary>
    )
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  composeButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },

  chatHeaderOverride: {
    paddingHorizontal: 0,
  },

  // Filter tabs styles — no horizontal padding; parent listContainer
  // already provides the 16px screen-edge inset.
  filterContainer: {
    paddingHorizontal: 0,
    paddingVertical: 8,
  },

  // Error state styles
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  errorMessage: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
    maxWidth: 300,
  },

  // Empty message styles (when tabs are visible but no data)
  emptyMessageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    // Optical centring via bottom padding, never a negative top margin: on web that
    // margin pulled this transparent box over the controls above it, which then
    // swallowed every tap (see emptyStateOverflow.test.ts).
    paddingBottom: 60,
    paddingHorizontal: 24,
  },
  emptyMessageText: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessageSubtext: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },

  noResultsContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: 120,
    paddingHorizontal: 20,
  },
  noResultsTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  noResultsMessage: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 300,
  },
});
